import { CoordinatorService } from './coordinator.service';
import { ScriptOutput } from './script-output';
import { ScriptItem } from './script-x-item';
import { XItem } from './x-item';

export class Script {
    id: string;
    title: string;
    onStart = false;
    inItemList = false;
    inItemDetail = false;
    isConnector = false;
    isSummary = false;
    isAsynchronous = false;
    weight = 1;

    private _state = false;
    private _source = '';
    private _fun: Function | undefined;

    private _variableMap = new Map<string, string>();

    constructor(
        public coords: CoordinatorService,
        {
            id,
            source,
            title,
            onStart,
            inItemList,
            inItemDetail,
            isConnector,
            isSummary,
            isAsynchronous,
            weight,
        }: {
            id?: string;
            source: string;
            title?: string;
            onStart?: boolean;
            inItemList?: boolean;
            inItemDetail?: boolean;
            isConnector?: boolean;
            isSummary?: boolean;
            isAsynchronous?: boolean;
            weight?: number;
        },
    ) {
        this.id = id || this.coords.uniqueId();
        this.title = title || '';
        this.source = source;
        this.onStart = onStart || false;
        this.inItemList = inItemList || false;
        this.inItemDetail = inItemDetail || false;
        this.isConnector = isConnector || false;
        this.isSummary = isSummary || false;
        this.isAsynchronous = isAsynchronous || false;
        this.weight = weight || 1;
    }

    setVariable(key: string, value: any) {
        this._variableMap.set(key, JSON.stringify(value));
        console.log('SET VARIABLE', key, value, this._variableMap);
    }

    deleteVariable(key: string) {
        this._variableMap.delete(key);
    }

    get variableLs() {
        return Array.from(this._variableMap).map((kv) => ({ name: kv[0], value: kv[1] }));
    }

    public get source(): string {
        return this._source;
    }
    public set source(value: string) {
        this._source = value;
    }

    get state(): boolean {
        return this._state;
    }

    private get _funBody() {
        const ls = [];

        for (let f of `dataset,databinList,hitset,
            subset, attachment, context, now, log, selection,notepad,
        stage,keyword,keywordGroup,settings, citation,
            ratings, tags, report, active, search, interface,
            saveAsBinary, saveAsText, saveAsJson, xml, html,
            searchArchive, batchManager,
            input, output,
            close,`.split(',')) {
            f = f.trim();
            f && ls.push(`const ${f} = $fun.${f}`);
        }
        ls.push('const $collection = dataset(false)');
        ls.push('const $preview =  dataset(true)');
        ls.push('const $active =  active()');
        ls.push('const $context =  context($params)');
        ls.push('const $project = $context.project');
        ls.push('const $input =  $context.input');
        ls.push('const $searchArchive =  searchArchive()');
        ls.push('const $batchManager = batchManager()');
        ls.push('const $settings = settings()');
        this._variableMap.forEach((v, k) => ls.push(`const ${k} = JSON.parse(${v})`));
        ls.push(this.source);
        return ls.join(';\n');
    }

    run(params?: { [tag: string]: any }) {
        if (!this._fun) this._runFun(params);
        if (!this._fun) return;
        const resp = this._fun(this.coords.script$.availableFunctions, params);

        return resp;
    }

   
    async runAsync(params?: { [tag: string]: any }) {
        if (!this._fun) this._runFun(params);
        if (!this._fun) return;
        return this._fun(this.coords.script$.availableFunctions, params);
    }

    private _runFun(params?: { [tag: string]: any }) {
        if (params) {
            for (const tag in params) {
                if (params[tag] === undefined) delete params[tag];
                else if (params[tag] instanceof XItem) params[tag] = params[tag].scriptClass;
            }
        }

        let funBody = this._funBody + '\n';
        try {
            this.coords.script$.hasScriptInRun = true;
            if (this.isAsynchronous)
                funBody = `return (async function(${params}) { ${funBody} })()`;

            if (!this._fun) {
                try {
                    this._fun = new Function('$fun', '$params', funBody);
                } catch (e) {
                    console.log(this.title, 'COMPILATION ERROR', e);
                }
            }
        } catch (e) {
            console.log(this.title, 'RUN ERROR', e);
            console.log(funBody);
            this._fun = undefined;
        } finally {
            this.coords.script$.hasScriptInRun = false;
        }

        // console.log("CONTEXT", context, this._funBody)
        // this._fun && this._fun(contextProxy, params)
    }

    save(
        title: string,
        data: string,
        {
            onStart,
            inItemList,
            inItemDetail,
            isConnector,
            isSummary,
            isAsynchronous,
            weight,
        }: {
            onStart?: boolean;
            inItemList?: boolean;
            inItemDetail?: boolean;
            isConnector?: boolean;
            isSummary?: boolean;
            isAsynchronous?: boolean;
            weight?: number;
        },
    ) {
        if (weight === undefined) weight = 1;

        this.title = title;
        this.source = data;
        this.onStart = onStart || false;
        this.inItemDetail = inItemDetail || false;
        this.inItemList = inItemList || false;
        this.isConnector = isConnector || false;
        this.isSummary = isSummary || false;
        this.isAsynchronous = isAsynchronous || false;
        this.weight = isNaN(weight) ? 1 : weight;
        this.coords.script$.add(this);
        this.coords.script$.refreshInLists();
        this._fun = undefined;
        this.coords.script$.markForCheck();
    }

    delete() {
        this.coords.script$.delete(this);
    }

    get data() {
        return {
            id: this.id,
            title: this.title,
            source: this.source,
            onStart: this.onStart,
            inItemDetail: this.inItemDetail,
            inItemList: this.inItemList,
            isConnector: this.isConnector,
            isSummary: this.isSummary,
            isAsynchronous: this.isAsynchronous,
            weight: this.weight,
        };
    }
}
